import React, { useMemo, useState } from 'react';
import affliateData from '../data/affiliate_directory.json';
import AdSpace from '../components/AdSpace';

const AffliateDirectory = ({ selectedCategory, onCategorySelect }) => {
  const [searchQuery, setSearchQuery] = useState('');

  // 1. Data Preparation - Map categories from the affiliate data structure
  const categories = affliateData.data.map(d => d.category);

  // 2. Map platforms using the 'programs' key and 'company' name from JSON
  const allPlatforms = useMemo(() => {
    return affliateData.data.flatMap(catGroup => 
      catGroup.programs.map(p => ({ 
        ...p, 
        // Ensure name consistency for filtering
        name: p.company, 
        parentCategory: catGroup.category 
      }))
    );
  }, []);

  const filteredItems = useMemo(() => {
    return allPlatforms.filter(p => {
      const matchesCategory = selectedCategory === 'All' || p.parentCategory === selectedCategory;
      const matchesSearch = p.company.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            p.commission.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [selectedCategory, searchQuery, allPlatforms]);

  return (
    <div className="flex flex-col lg:flex-row min-h-screen bg-slate-50 font-sans">
      
      {/* --- SIDEBAR: Fixed stability --- */}
      <aside className="w-full lg:w-72 bg-white border-r border-slate-200 
                        lg:fixed lg:top-16 lg:bottom-0 lg:left-0 
                        overflow-y-auto z-30 transition-all shadow-sm">
        <div className="p-8">
          <div className="mb-8">
            <h2 className="text-blue-600 font-black text-xl tracking-tighter">MINDSLOG</h2>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-[0.2em]">Affliate Hub</p>
          </div>

          <nav className="space-y-1 pb-10">
            <button
              onClick={() => onCategorySelect('All')}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-bold transition-all cursor-pointer ${
                selectedCategory === 'All' ? 'bg-slate-900 text-white shadow-lg' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <span>All </span>
              <span className={`text-[10px] px-2 py-0.5 rounded-md ${selectedCategory === 'All' ? 'bg-slate-700' : 'bg-slate-100'}`}>
                {allPlatforms.length}
              </span>
            </button>

            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => onCategorySelect(cat)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-bold transition-all cursor-pointer ${
                  selectedCategory === cat ? 'bg-blue-600 text-white shadow-md' : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <span className="truncate mr-2">{cat}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-md ${selectedCategory === cat ? 'bg-blue-500' : 'bg-slate-100'}`}>
                  {affliateData.data.find(d => d.category === cat)?.programs.length}
                </span>
              </button>
            ))}
          </nav>
        </div>
      </aside>

      {/* --- MAIN CONTENT --- */}
      <main className="flex-1 p-6 lg:p-12 lg:ml-72 min-h-screen">
        <div className="max-w-6xl mx-auto">
          {/* Header Section */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
            <div>
              <p className="text-blue-600 font-bold text-sm mb-2 uppercase tracking-widest">Marketplace Index</p>
              <h1 className="text-4xl font-black text-slate-900 tracking-tight">
                {selectedCategory}
              </h1>
            </div>
            
            <div className="relative w-full md:w-96">
              <input
                type="text"
                placeholder="Search platforms..."
                className="w-full pl-12 pr-4 py-3 bg-white border border-slate-200 rounded-2xl shadow-sm focus:ring-4 focus:ring-blue-100 outline-none transition-all"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <svg className="absolute left-4 top-3.5 h-5 w-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          {/* List View Container */}
          <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="hidden md:grid grid-cols-12 gap-4 px-8 py-4 bg-slate-50 border-b border-slate-200 text-[11px] font-black text-slate-400 uppercase tracking-widest">
              <div className="col-span-3">Company Name</div>
              <div className="col-span-2">Commission</div>
              <div className="col-span-5">Program Details</div>
              <div className="col-span-2 text-right">Action</div>
            </div>

            <div className="divide-y divide-slate-100">
              {filteredItems.map((item, index) => (
                <div key={index} className="group md:grid md:grid-cols-12 gap-4 px-8 py-6 items-center hover:bg-blue-50/30 transition-colors">
                  <div className="col-span-3">
                    <h3 className="text-lg font-bold text-slate-900 group-hover:text-blue-700 transition-colors">
                      {item.company}
                    </h3>
                  </div>
                  <div className="col-span-2">
                    <span className="inline-block px-2.5 py-1 rounded-md bg-green-50 text-green-700 text-[10px] font-black uppercase tracking-tighter">
                      {item.commission}
                    </span>
                  </div>
                  <div className="col-span-5 text-slate-500 text-sm leading-relaxed">
                    {item.payout_type} • {item.cookie_days} Day Cookie • Min Payout: {item.min_payout}
                  </div>
                  <div className="col-span-2 text-right">
                    <a
                      href={item.link}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-slate-900 text-white text-xs font-bold rounded-xl hover:bg-blue-600 transition-all shadow-md"
                    >
                      Join Program
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
      <div className="lg:w-72 border-l border-slate-100 bg-white">
        <AdSpace />
      </div>
    </div>
  );
};

export default AffliateDirectory;